import type { FragmentType } from "@repo/graphql-types/fragment-masking";
import { useFragment } from "@repo/graphql-types/fragment-masking";
import { graphql } from "@repo/graphql-types/gql";
import { Link, useRouteContext } from "@tanstack/react-router";
import { Edit1, Transfer, User1 } from "@repo/icons";
import { useMemo } from "react";
import { format } from "date-fns";
import { formatDateOfBirth } from "@/lib/string";
import type { Info } from "@/components/list/item";
import { InformationalList } from "@/components/list/informational-list";
import {
  CopyableFieldValue,
  InfoSectionV2,
  type InfoSectionV2Props,
} from "@/components/info-section-v2";
import { HealthInsuranceInfo } from "@/components/health-insurance-info";
import { getBirthDateWithAge } from "@/lib/get-birthday-with-age";

export const PatientDetailPropertyListFragment = graphql(/* GraphQL */ `
  fragment PatientDetailPropertyListFragment on pacientes {
    cpf
    email
    dataNascimento
    telefone
    telefoneInternacionalNormalizado
    receberWhatsAppConfirmacao
    nomeConvenio
    nomePlano
    numeroCarteirinha
    validadeCarteirinha
    codTipoConvenio
    codPaciente
  }
`);

interface PatientDetailPropertyListProps {
  patient: FragmentType<typeof PatientDetailPropertyListFragment>;
  title?: string;
}

export const PatientDetailPropertyList = (
  props: PatientDetailPropertyListProps,
): JSX.Element => {
  const { flags } = useRouteContext({ strict: false });

  const showNewPatientLayout = useMemo(
    () => flags["exibe-nova-tela-de-paciente"],
    [flags],
  );
  const isInternationalPhoneActive = useMemo(
    () => flags["altera-telefone-por-telefone-internacional"],
    [flags],
  );

  const patient = useFragment(PatientDetailPropertyListFragment, props.patient);

  const infos: Info[] = [
    {
      label: "Data de nascimento",
      value: patient.dataNascimento ? formatDateOfBirth(patient.dataNascimento) : "-",
    },
    {
      label: "Telefone",
      value: isInternationalPhoneActive
        ? patient.telefoneInternacionalNormalizado ?? patient.telefone
        : patient.telefone,
    },
    {
      label: "Email",
      value: patient.email ?? "-",
    },
    {
      label: "CPF",
      value: patient.cpf ?? "-",
    },
  ];

  if (showNewPatientLayout) {
    return (
      <div className="mt-6">
        <InformationalList title={props.title ?? "Informações"} infos={infos} />
      </div>
    );
  }

  const patientInfoSection: InfoSectionV2Props = {
    section: {
      Icon: User1,
      title: "Dados pessoais",
      action: (
        <Link
          to="/patients/$patientId/update"
          params={{ patientId: String(patient.codPaciente) }}
        >
          <button type="button" className="bg-secondary-400 rounded-full p-2">
            <Edit1 size={16} className="fill-white !h-5 !w-5" />
          </button>
        </Link>
      ),
      fields: [
        {
          label: "Data de nascimento",
          value: patient.dataNascimento
            ? getBirthDateWithAge(patient.dataNascimento)
            : "-",
        },
        {
          label: "Contato",
          value: (
            <CopyableFieldValue
              field="Contato"
              value={
                isInternationalPhoneActive
                  ? patient.telefoneInternacionalNormalizado ?? patient.telefone
                  : patient.telefone
              }
            />
          ),
        },
        {
          label: "Confirmação por WhatsApp",
          value: patient.receberWhatsAppConfirmacao ? "Ativo" : "Inativo",
        },
        {
          label: "Email",
          value: <CopyableFieldValue field="Email" value={patient.email} />,
        },
        {
          label: "CPF",
          value: <CopyableFieldValue field="CPF" value={patient.cpf} />,
        },
      ],
    },
  };

  const healthPlanInfoSection: InfoSectionV2Props = {
    section: {
      Icon: Transfer,
      title: "Dados do convênio",
      fields: [
        {
          label: "Convênio",
          value: (
            <HealthInsuranceInfo
              healthInsuranceName={patient.nomeConvenio ?? patient.nomePlano}
              healthInsuranceCode={patient.codTipoConvenio}
            />
          ),
        },
        {
          label: "Carteirinha",
          value: (
            <CopyableFieldValue field="Carteirinha" value={patient.numeroCarteirinha} />
          ),
        },
        {
          label: "Validade do plano",
          value: patient.validadeCarteirinha
            ? format(new Date(patient.validadeCarteirinha), "MM/yyyy")
            : "-",
        },
      ],
    },
  };

  return (
    <div className="mt-8 flex flex-col gap-6">
      <InfoSectionV2 {...patientInfoSection} />
      {patient.codTipoConvenio ? <InfoSectionV2 {...healthPlanInfoSection} /> : null}
    </div>
  );
};
